import { mapDeviceAuthenticationFromSysApi } from '@shure/shared/angular/data-access/device-authentication';
import { ConfigInput8021X, EapMethod8021X } from '@shure/shared/angular/data-access/system-api/core';
import {
	mapDeviceLastAuthenticationStatus8021xFromSysApi,
	mapDeviceModelFromSysApi,
	mapNetworkModeFromSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';

import { CertificateInfo, Sh8021xConfig, Sh8021xMethod, Sh8021xSettingsDevice } from '../../api';
import {
	Security8021XCertificateInfoFragment,
	Security8021XDeviceSettingsFragment
} from '../graphql/generated/8021x.generated';

export function map8021xSettingsDeviceFromSysApi(
	deviceSysApi: Security8021XDeviceSettingsFragment,
	authenticated: boolean | false,
	readonlyModeSelected: boolean | false
): Sh8021xSettingsDevice {
	const {
		enabled,
		rebootRequired,
		eapMethod,
		username,
		anonymousId,
		lastAuthenticationStatus,
		clientCertificate,
		rootCertificate
	} = deviceSysApi.features.security8021X!;

	const { authentication, networkSwitchConfiguration } = deviceSysApi.features;

	const device: Sh8021xSettingsDevice = {
		id: deviceSysApi.id,
		model: mapDeviceModelFromSysApi(deviceSysApi.description.model),
		status: deviceSysApi.status,
		sh8021xConfig: {
			isEnabled: enabled,
			isRebootRequired: rebootRequired,
			method: mapEapMethodFromSysApi(eapMethod),
			username: username ?? '',
			anonymousId: anonymousId ?? '',
			lastAuthenticationStatus: mapDeviceLastAuthenticationStatus8021xFromSysApi(lastAuthenticationStatus)
		},
		features: {
			authentication: mapDeviceAuthenticationFromSysApi(authentication, authenticated, readonlyModeSelected),
			networkSwitchConfiguration: deviceSysApi.description.features.networkSwitchConfiguration.supported
				? {
						isMissing: !networkSwitchConfiguration,
						mode: mapNetworkModeFromSysApi(networkSwitchConfiguration?.mode)
					}
				: undefined
		}
	};

	if (clientCertificate) {
		device.sh8021xConfig.clientCertInfo = map8021XCertificateFromSysApi(clientCertificate);
	}

	if (rootCertificate) {
		device.sh8021xConfig.rootCertInfo = map8021XCertificateFromSysApi(rootCertificate);
	}

	return device;
}

export function map8021XCertificateFromSysApi(certificate: Security8021XCertificateInfoFragment): CertificateInfo {
	return {
		expirationDate: certificate.expirationDate,
		hash: certificate.hash ?? '',
		issuer: certificate.issuer ?? '',
		validFromDate: certificate.validFromDate
	};
}

export function map8021XConfigToSysApi(config: Sh8021xConfig): ConfigInput8021X {
	const { passphrase = '', method, username, anonymousId = '', clientCertInfo, rootCertInfo } = config;

	if (method === Sh8021xMethod.None) {
		return <ConfigInput8021X>{};
	}

	// Fix this so only the correct properties are required: https://jira.shure.com/browse/SHSYSAPI-7995
	switch (method) {
		case Sh8021xMethod.Tls:
			return {
				tls: {
					username,
					anonymousId,
					clientCertificate: clientCertInfo!.certificateBase64String!,
					rootCertificate: rootCertInfo!.certificateBase64String!,
					passphrase
				}
			};

		case Sh8021xMethod.TtlsGtc:
			return {
				ttlsGtc: {
					username,
					anonymousId,
					clientCertificate: clientCertInfo?.certificateBase64String,
					rootCertificate: rootCertInfo!.certificateBase64String!,
					passphrase
				}
			};

		case Sh8021xMethod.TtlsMd5:
			return {
				ttlsMd5: {
					username,
					anonymousId,
					clientCertificate: clientCertInfo?.certificateBase64String,
					rootCertificate: rootCertInfo!.certificateBase64String!,
					passphrase
				}
			};

		case Sh8021xMethod.TtlsMschapV2:
			return {
				ttlsMschapv2: {
					username,
					anonymousId,
					clientCertificate: clientCertInfo?.certificateBase64String,
					rootCertificate: rootCertInfo!.certificateBase64String!,
					passphrase
				}
			};

		case Sh8021xMethod.TtlsPap:
			return {
				ttlsPap: {
					username,
					anonymousId,
					clientCertificate: clientCertInfo?.certificateBase64String,
					rootCertificate: rootCertInfo!.certificateBase64String!,
					passphrase
				}
			};

		case Sh8021xMethod.Md5:
			return {
				eapMd5: {
					username,
					passphrase
				}
			};

		case Sh8021xMethod.PasswordOnly:
			return {
				pwd: {
					username,
					passphrase
				}
			};

		case Sh8021xMethod.PeapGtc:
			return {
				peapGtc: {
					username,
					anonymousId,
					clientCertificate: clientCertInfo?.certificateBase64String,
					rootCertificate: rootCertInfo!.certificateBase64String!,
					passphrase
				}
			};

		case Sh8021xMethod.PeapMschapV2:
			return {
				peapMschapv2: {
					username,
					anonymousId,
					clientCertificate: clientCertInfo?.certificateBase64String,
					rootCertificate: rootCertInfo!.certificateBase64String!,
					passphrase
				}
			};
	}
}

export function mapEapMethodFromSysApi(eapMethod: EapMethod8021X): Sh8021xMethod {
	switch (eapMethod) {
		case EapMethod8021X.Tls:
			return Sh8021xMethod.Tls;
		case EapMethod8021X.Disabled:
			return Sh8021xMethod.None;
		case EapMethod8021X.EapMd5:
			return Sh8021xMethod.Md5;
		case EapMethod8021X.PeapGtc:
			return Sh8021xMethod.PeapGtc;
		case EapMethod8021X.PeapMschapv2:
			return Sh8021xMethod.PeapMschapV2;
		case EapMethod8021X.Pwd:
			return Sh8021xMethod.PasswordOnly;
		case EapMethod8021X.TtlsGtc:
			return Sh8021xMethod.TtlsGtc;
		case EapMethod8021X.TtlsMd5:
			return Sh8021xMethod.TtlsMd5;
		case EapMethod8021X.TtlsMschapv2:
			return Sh8021xMethod.TtlsMschapV2;
		case EapMethod8021X.TtlsPap:
			return Sh8021xMethod.TtlsPap;
	}
}
